import React, { useMemo, useState } from "react";

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import { UsageWindowSize } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import AIBurndownChart from "./ai-burndown-chart";
import { PERIOD_COUNT, buildPeriods } from "./credit-periods";
import { coalesceToDaily, useOrgAIBurndown } from "./use-org-ai-burndown";
import { UsageWindow } from "./use-org-ai-credit";
import UserAIBurndownSection from "./user-ai-burndown-section";
import GenericDialog from "components/shared/generic-dialog";

type BurndownScope = "org" | "user";

interface AIBurndownModalProps {
  open: boolean;
  onClose: () => void;
  roleId: string;
  regionCode: string;
  usageWindow: UsageWindow;
  ready: boolean;
  // Which scopes the viewer is permitted to see. The scope toggle is only shown
  // when both are available; otherwise the single permitted scope is rendered.
  canViewOrg: boolean;
  canViewUser: boolean;
}

// AIBurndownModal shows the AI credit burndown chart in a dialog, with a toggle
// between the org-wide view (pack-type breakdown) and the per-user view (Free vs
// Paid, with a user picker), and a picker to view the current or a prior AI
// credit period. Data is only fetched while the modal is open, so opening the AI
// tab no longer pays for a burndown query up front.
const AIBurndownModal = ({
  open,
  onClose,
  roleId,
  regionCode,
  usageWindow,
  ready,
  canViewOrg,
  canViewUser,
}: AIBurndownModalProps) => {
  const [scope, setScope] = useState<BurndownScope>(
    canViewOrg ? "org" : "user",
  );
  const [orgWindowSize, setOrgWindowSize] = useState<UsageWindowSize>(
    UsageWindowSize.DAY,
  );
  const [periodIndex, setPeriodIndex] = useState(0);

  const periods = useMemo(
    () => buildPeriods(usageWindow, PERIOD_COUNT),
    [usageWindow],
  );
  const selectedPeriod = periods[periodIndex] ?? usageWindow;

  const {
    isLoading: isOrgLoading,
    windows: orgHourly,
    summary: orgSummary,
  } = useOrgAIBurndown(
    roleId,
    regionCode,
    selectedPeriod,
    open && ready && canViewOrg && scope === "org",
  );

  // Always fetched hourly; coalesce to daily for the daily view.
  const orgWindows = useMemo(
    () =>
      orgWindowSize === UsageWindowSize.DAY
        ? coalesceToDaily(orgHourly)
        : orgHourly,
    [orgHourly, orgWindowSize],
  );

  const showScopeToggle = canViewOrg && canViewUser;

  return (
    <GenericDialog
      title="Credit burndown"
      open={open}
      handleDialogClose={onClose}
      hasXCloseBtn
      width="lg"
    >
      <Box sx={{ width: "100%", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: showScopeToggle ? "space-between" : "flex-end",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mx: "20px",
            pt: 1,
            pb: 3,
          }}
        >
          {showScopeToggle && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(_, value) => value != null && setScope(value)}
              data-testid="burndown-scope-toggle"
            >
              <ToggleButton value="org">Organization</ToggleButton>
              <ToggleButton value="user">Per user</ToggleButton>
            </ToggleButtonGroup>
          )}

          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="burndown-period-label">Credit period</InputLabel>
            <Select
              labelId="burndown-period-label"
              label="Credit period"
              value={periodIndex}
              onChange={(e) => setPeriodIndex(Number(e.target.value))}
              data-testid="burndown-period-select"
            >
              {periods.map((p, i) => (
                <MenuItem key={i} value={i}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {scope === "org" ? (
          <AIBurndownChart
            windows={orgWindows}
            windowSize={orgWindowSize}
            onWindowSizeChange={setOrgWindowSize}
            isLoading={isOrgLoading}
            summary={orgSummary}
          />
        ) : (
          <UserAIBurndownSection
            roleId={roleId}
            regionCode={regionCode}
            usageWindow={selectedPeriod}
            ready={open && ready}
          />
        )}
      </Box>
    </GenericDialog>
  );
};

export default AIBurndownModal;
